import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageCtaSectionConfig = {
  enabled?: boolean;
  title?: LocalizedString;
  text?: LocalizedString;
  buttonText?: LocalizedString;
  buttonUrl?: string;
  backgroundImage?: string;
  align?: 'left' | 'center';
  variant?: 'gradient' | 'soft';
};

type Props = {
  config?: PageCtaSectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const PageCtaSection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const cfg: PageCtaSectionConfig = (config && typeof config === 'object') ? config : {};
  const { language } = useTranslation();
  if (cfg.enabled === false) return null;

  const title = getLocalizedString(cfg.title, language, 'en', 'Bereit loszulegen?');
  const text = getLocalizedString(cfg.text, language, 'en', 'Entdecke unsere Highlights oder starte direkt mit deinem nächsten Fund.');
  const buttonText = getLocalizedString(cfg.buttonText, language, 'en', '');
  const buttonUrl = typeof cfg.buttonUrl === 'string' ? cfg.buttonUrl : '';
  const align: 'left' | 'center' = cfg.align === 'left' ? 'left' : 'center';
  const variant: 'gradient' | 'soft' = cfg.variant === 'soft' ? 'soft' : 'gradient';

  const bg = typeof cfg.backgroundImage === 'string' ? cfg.backgroundImage : '';
  const bgUrl = bg ? (resolvePublicUrl ? resolvePublicUrl(bg) : bg) : '';

  const clickable = !!(buttonText && buttonUrl);

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            position: 'relative',
            borderRadius: { xs: 4, md: 5 },
            overflow: 'hidden',
            px: { xs: 2.5, sm: 4, md: 6 },
            py: { xs: 4, sm: 5, md: 6 },
            bgcolor: variant === 'soft' ? 'background.paper' : 'grey.900',
            boxShadow: variant === 'soft' ? '0 16px 50px rgba(2,6,23,0.10)' : '0 26px 70px rgba(2,6,23,0.25)',
            border: variant === 'soft' ? '1px solid' : undefined,
            borderColor: variant === 'soft' ? 'divider' : undefined,
            color: variant === 'soft' ? 'text.primary' : 'common.white',
          }}
        >
          {bgUrl ? (
            <Box
              component="img"
              src={bgUrl}
              alt="CTA background"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: variant === 'soft' ? 'brightness(0.95)' : 'brightness(0.70)' }}
            />
          ) : null}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: variant === 'soft'
                ? 'radial-gradient(900px 420px at 10% 20%, rgba(59,130,246,0.12), transparent), radial-gradient(900px 420px at 90% 70%, rgba(99,102,241,0.12), transparent)'
                : 'radial-gradient(900px 420px at 10% 20%, rgba(59,130,246,0.38), transparent), radial-gradient(900px 420px at 90% 70%, rgba(236,72,153,0.28), transparent), linear-gradient(180deg, rgba(0,0,0,0.20), rgba(0,0,0,0.55))',
            }}
          />

          <Box sx={{ position: 'relative', display: 'grid', gap: 2, textAlign: align, justifyItems: align === 'center' ? 'center' : 'start' }}>
            <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: -0.4, lineHeight: 1.1, textShadow: variant === 'soft' ? 'none' : '0 10px 34px rgba(0,0,0,0.30)' }}>
              {title}
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 820, color: variant === 'soft' ? 'text.secondary' : 'rgba(255,255,255,0.88)', textShadow: variant === 'soft' ? 'none' : '0 10px 34px rgba(0,0,0,0.30)' }}>
              {text}
            </Typography>

            {clickable ? (
              <Box sx={{ pt: 0.5 }}>
                <Button
                  variant="contained"
                  href={buttonUrl}
                  sx={{
                    borderRadius: 999,
                    px: 3.25,
                    py: 1.25,
                    fontWeight: 900,
                    textTransform: 'none',
                    boxShadow: variant === 'soft' ? '0 14px 34px rgba(59,130,246,0.25)' : '0 18px 50px rgba(59,130,246,0.40)',
                    background: variant === 'soft' ? undefined : 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                    '&:hover': {
                      background: variant === 'soft' ? undefined : 'linear-gradient(90deg, #3b82f6, #2563eb)',
                      boxShadow: variant === 'soft' ? '0 16px 38px rgba(37,99,235,0.24)' : '0 22px 60px rgba(37,99,235,0.42)',
                    },
                  }}
                >
                  {buttonText}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PageCtaSection;
